import { Attachment } from "types/common.types";

import { UploadedFile } from "components/Common/FileUploader";
import { SelectOptions } from "components/Common/Select";

import { PivotUser, Role, User } from "store/auth/types";
import { SingleMeeting } from "store/meetings/types";

export type SingleVoting = {
  attachments: any;
  description: string | null;
  end_date: string;
  results: VotingFinalResult | null;
  id: number;
  invitations_met_quorum: boolean;
  name: string;
  quorum_type: QuorumTypes;
  start_date: string;
  status: VotingStatus;
  type: VotingType;
  users: PivotUser[];
  count_shares: boolean;
  secrecy: string;
  ignore_creator_vote: boolean;
  options: VotingOption[];
  ongoing_results: {
    options: OngoingVotingOption[];
  };
  invitation_status: string;
  reason: string;
  votes: Vote[];
  meeting_id: number | null;
  meeting: Partial<SingleMeeting> | null;
  meeting_relation: MeetingRelation | null;
  requires_room: boolean;
  notes?: SingleVotingNote[];
  creator?: User;
  conclusion?: string;
  workspace_id?: number | null;
  workspace_name?: string | null;
  has_uploaded_summary?: boolean;
  users_can_vote: number[];
  cumulative_voting_target?: number;
  max_selected_options?: number;
};

export type VotingReviewer = {
  id: number;
  name: string;
  comment: string;
  seen_at: string;
  created_at: string;
  updated_at?: string;
  status: VotingStatus;
};

export type VotingFinalResult = {
  created_at: string;
  has_presence_quorum: boolean;
  id: number;
  status: string;
  updated_at: string;
  votes_abstained_count: number;
  votes_count: number;
  voting_id: number;
  abstained: User[];
  details: VotingFinalResultDetailsOption[];
  reviewers?: VotingReviewer[];
  signit_signatory_id?: string | null;
  signit_signature_request_id?: string | null;
  signit_document_id?: string | null;
};

export type VotingFinalResultDetailsOption = {
  has_quorum: boolean;
  id: number;
  option: string;
  shares: number;
  users: User[];
  votes: number;
  votes_count: number;
  option_id: number;
  shares_percentage: number;
  votes_percentage: number;
  votes_count_percentage: number;
};

export type Vote = {
  id: number;
  voting_id: number;
  original_user: User;
  status: string;
  option: VotingOption;
};

export enum VotingStatus {
  DRAFT = "draft",
  ACCEPTED = "accepted",
  APPROVED = "approved",
  STARTED = "started",
  ENDED = "ended",
  VOTING_ONGOING = "voting_ongoing",
  WAITING_FOR_APPROVAL = "waiting",
  AWAITING_SIGNATURE = "awaiting_signature",
  SIGNED = "signed",
  REJECTED = "rejected",
}

export enum VotingType {
  SINGLE = "single",
  MULTIPLE = "multiple",
}

export type OngoingVotingOption = {
  id: number;
  option: string;
  votes: number;
  users: User[];
};

export type VotingOption = {
  id: number;
  option: string;
  order?: number;
  voting_id: number;
};

export enum VotingUserRole {
  VOTING_OWNER = "voting_owner",
  VOTING_VOTER = "voting_voter",
  VOTING_COMMENTATOR = "voting_commentator",
  VOTING_VIEWER = "voting_viewer",
  VOTING_ELIMINATED = "voting_eliminated",
}

export type VotingUser = {
  id: number;
  role: VotingUserRole;
};

export enum MeetingRelation {
  BEFORE = "before",
  DURING = "during",
}

export type VotingFormData = {
  id?: number;
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  type: VotingType;
  status: VotingStatus;
  quorum_type: string;
  users: {
    id: number;
    role: string;
  }[];
  options: VotingOption[];
  secrecy: VotingSecrecy;
  cumulative_voting_target?: number;
  max_selected_options?: number;
  meeting_relation?: MeetingRelation;
  requires_room?: boolean;
  workspace_id?: number;
  ignore_creator_vote: boolean;
  files: UploadedFile[];
  serverFiles: Attachment[];
};

export enum VotingSecrecy {
  SECRET = "secret",
  OPEN = "open",
}

export type VotingFormUsers = { user: User; role: SelectOptions };

export type VotingFormProps = {
  id?: number;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  quorumType: SelectOptions;
  options: VotingOption[];
  users: VotingFormUsers[];
  type: SelectOptions;
  maxSelectOptions: SelectOptions;
  secrecy: boolean;
  cumulativeVotingTarget?: SelectOptions;
  requires_room?: boolean;
  workspace: SelectOptions;
  ignoreCreatorVote: boolean;
  files: UploadedFile[];
  serverFiles: Attachment[];
};

export type VotingContentUser = {
  invitation_status: string;
  pivot_id: number;
  reason: string | null;
  role_id: number;
  user: User;
  user_id: number;
  voting_id: number;
  voting_role: Role;
};

export type MeetingSubsectionVotingForm = {
  id?: number;
  name: string;
  quorumType: SelectOptions;
  description: string;
  options: VotingOption[];
  users: VotingFormUsers[];
  speakers: User[];
  duration: number;
  secrecy?: boolean;
  cumulativeVotingTarget?: SelectOptions;
  type?: SelectOptions;
  maxSelectOptions?: SelectOptions;
  files: UploadedFile[];
  serverFiles: Attachment[];
  ignoreCreatorVote: boolean;
};

export type VotingsFilters = Record<string, string>;

export enum VotingInvitationStatus {
  NEW = "new",
  ACCEPTED = "accepted",
  REJECTED = "rejected",
}

export type UserBallots = {
  id: number;
  original_user: User;
  shares: number;
  voting_id: number;
  voting_user: User;
  weight: number;
};

export enum QuorumTypes {
  MAJORITY_SHARES = "majority_shares",
  MAJORITY_NUMBER = "majority_number",
  MAJORITY_MIXED = "majority_mixed",
  CUMULATIVE_VOTING = "cumulative_voting",
}

export enum VotingUserStatus {
  NEW = "new",
  REJECTED = "rejected",
  ACCEPTED = "accepted",
}

export type VotingNoteUpdateData = {
  votingId: string;
  note: string;
  noteId: string;
};

export type VotingNoteSaveData = {
  votingId: string;
  note: string;
};

export type SingleVotingNote = {
  created_at: string;
  creator_id: number;
  id: number;
  note: string;
  updated_at: string;
  voting_id: number;
};

export enum VotingMeetingRelations {
  ALL = "all",
  IS_DURING_MEETING = "is_during_meeting",
  IS_NOT_DURING_MEETING = "is_not_during_meeting",
}
